import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import moment from "moment";
import { PhoneInput } from "../../components/PhoneInput";
import { useCart } from "../../contexts/CartContext";
import { useGTM } from "../../hooks/useGTM";
import { createClientOnline, requestVerificationCode, validateVerificationCode, resendVerificationCodeEmail } from "../../services/clientService";
import { getPickupAddress } from "../../services/addressService";
import { createOrder } from "../../services/orderService";
import { createMercadoPagoPreference } from "../../services/mercadoPagoService";
import { Loader } from "../../components/Loader";
import { ErrorAlert } from "../../components/ErrorAlert";
import { SuccessAlert } from "../../components/SuccessAlert";
import { WelcomeBackModal } from "../../components/WelcomeBackModal";
import { RememberCardModal } from "../../components/RememberCardModal";
import { TooltipModal } from "../../components/Tooltip";
import { StoreFrontHeader } from "../../components/StoreFrontHeader";
import { convertMoney, rawTelephone } from "../../utils";
import { TYPES_OF_DELIVERY } from "../../constants";

import {
    FormField,
    Label,
    Input,
    Select,
    Textarea,
    InlineFormField,
    ErrorMessage,
    PrimaryButton,
    Form as GlobalForm
} from "../../styles/global";
import {
    Container,
    Content,
    CartSection,
    SectionTitle,
    OrderSummary,
    SummaryRow,
    CheckboxContainer,
    Checkbox,
    FormFieldTitle,
    ProductsList,
    ProductItem,
    ProductDetails,
    ProductName,
    ProductQuantity,
    ProductPrice,
    Divider,
    CartItemImage,
    CompletedOrder,
    StepperContainer,
    StepperWrapper,
    Step,
    StepCircle,
    StepLabel,
    StepSubLabel,
    ResendCodeLink,
} from "./style";

moment.locale('pt-br');

interface Schedule {
    id: string;
    day_of_week: string;
    is_closed: boolean;
    opening_time: string | null;
    closing_time: string | null;
    lunch_break_start: string | null;
    lunch_break_end: string | null;
    created_at: string;
    updated_at: string;
}

interface INewOrder {
    first_name: string;
    last_name: string;
    phone_number: string;
    country_code: string;
    email: string;
    verification_code: string;
    addressId: string;
    pickup_on_store: boolean;
    type_of_delivery: string;
    postal_code: string;
    city: string;
    state: string;
    street: string;
    street_number: string;
    complement: string;
    reference_point: string;
    neighborhood: string;
    country: string;
    receiver_name: string;
    receiver_phone: string;
    receiver_country_code: string;
    delivery_date: string;
    has_card: boolean;
    card_message: string;
    card_from: string;
    card_to: string;
    payment_method: string;
}

export function Checkout() {
    const navigate = useNavigate();
    const { slug } = useParams<{ slug: string }>();
    const { cartItems, cartTotal, deliveryInfo, isDeliveryCalculated, appliedCoupon } = useCart();
    const { trackPageView, trackBeginCheckout, trackAddShippingInfo } = useGTM();
    const [showLoader, setShowLoader] = useState(false);
    const [countdown, setCountdown] = useState(30);
    const [addresses, setAddresses] = useState([]);
    const [client_id, setClientId] = useState("");
    const [addressId, setAddressId] = useState("");
    const [selectedAddress, setSelectedAddress] = useState<any>({});
    const [pickupOnStore, setPickupOnStore] = useState(false);
    const [newAddress, setNewAddress] = useState(false);
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);
    const [showRememberCardModal, setShowRememberCardModal] = useState(false);
    const [cardModalShowed, setCardModalShowed] = useState(false);
    const [showToolTipModal, setShowToolTipModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [currentOrder] = useState<any>(null);
    const cardSectionRef = useRef<HTMLDivElement>(null);
    const previousPhoneRef = useRef<string>("");
    const [awaitingVerification, setAwaitingVerification] = useState(false);
    const [verificationSentVia, setVerificationSentVia] = useState<"whatsapp" | "email">("whatsapp");
    const [resendingEmail, setResendingEmail] = useState(false);
    const [clientExists, setClientExists] = useState(false);
    const [formStarted, setFormStarted] = useState(false);
    const [welcomeClientName, setWelcomeClientName] = useState("");
    const [storeSchedules, setStoreSchedules] = useState<Schedule[]>([]);
    const [shippingInfoTracked, setShippingInfoTracked] = useState(false);
    const [maxDeliveryDaysAdvance, setMaxDeliveryDaysAdvance] = useState<number>(30);
    const tooltipMessage = `Para entregas em outras regiões,
        por favor entre em contato conosco pelo whatsapp.`;

    useEffect(() => {
        window.scrollTo(0, 0);
        // GTM - Track Page View
        trackPageView('Checkout');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // GTM - Track Begin Checkout
    useEffect(() => {
        if (cartItems.length > 0) {
            trackBeginCheckout(cartItems, cartTotal);
        }
    }, [cartItems, cartTotal, trackBeginCheckout]);

    const deliveryFee = pickupOnStore ? 0 : (deliveryInfo?.fee ?? 0);
    const couponDiscount = appliedCoupon?.discount_amount ?? 0;
    const totalWithDelivery = cartTotal + deliveryFee - couponDiscount;

    // GTM - Track Add Shipping Info quando endereço for selecionado/preenchido
    useEffect(() => {
        // Dispara quando:
        // 1. Endereço selecionado E frete calculado (entrega) OU
        // 2. Retirada na loja selecionada
        const shouldTrack = (addressId && deliveryInfo) || (pickupOnStore && addressId);
        
        if (shouldTrack && cartItems.length > 0 && !shippingInfoTracked) {
            const fee = pickupOnStore ? 0 : (deliveryInfo?.fee ?? 0);
            trackAddShippingInfo(cartItems, cartTotal, fee);
            setShippingInfoTracked(true);
        }
    }, [addressId, deliveryInfo, pickupOnStore, cartItems, cartTotal, shippingInfoTracked, trackAddShippingInfo]);

    // Reset do flag quando o endereço ou tipo de entrega mudar
    useEffect(() => {
        setShippingInfoTracked(false);
    }, [addressId, pickupOnStore]);

    const DAY_OF_WEEK_MAP: { [key: string]: number } = {
        'SUNDAY': 0,
        'MONDAY': 1,
        'TUESDAY': 2,
        'WEDNESDAY': 3,
        'THURSDAY': 4,
        'FRIDAY': 5,
        'SATURDAY': 6
    };

    const isValidDeliveryDate = (dateString: string): boolean => {
        if (!dateString || storeSchedules.length === 0) return true;

        const selectedDate = moment(dateString);
        const today = moment();
        const dayOfWeek = selectedDate.day();

        if (selectedDate.isBefore(today, 'day')) {
            return false;
        }

        // Validar se a data está dentro do limite de dias de antecedência
        const maxDate = moment().add(maxDeliveryDaysAdvance, 'days');
        if (selectedDate.isAfter(maxDate, 'day')) {
            return false;
        }

        const schedule = storeSchedules.find(s => {
            const scheduleDayNumber = DAY_OF_WEEK_MAP[s.day_of_week];
            return scheduleDayNumber === dayOfWeek;
        });

        if (!schedule || schedule.is_closed) {
            return false;
        }

        if (selectedDate.isSame(today, 'day') && schedule.closing_time) {
            const now = moment();
            const [closingHour, closingMinute] = schedule.closing_time.split(':').map(Number);
            const closingTime = moment().set({
                hour: closingHour,
                minute: closingMinute,
                second: 0,
                millisecond: 0
            });

            if (now.isSameOrAfter(closingTime)) {
                return false;
            }
        }

        return true;
    };

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        setError,
        clearErrors,
        control,
        formState: { errors, submitCount },
    } = useForm<INewOrder>({
        defaultValues: {
            country: "Brasil",
            state: deliveryInfo?.state || "",
            city: deliveryInfo?.city || "",
            postal_code: deliveryInfo?.cep || "",
            street: deliveryInfo?.street || "",
            neighborhood: deliveryInfo?.neighborhood || "",
            payment_method: "MERCADO_PAGO",
            country_code: "BR",
            receiver_country_code: "BR",
        }
    });

    const submitOrder = async (data: INewOrder) => {
        if (!data.has_card && data.type_of_delivery.includes("GIFT") && !cardModalShowed) {
            setCardModalShowed(true);
            setShowRememberCardModal(true);
            return;
        }

        if (cartItems.length === 0) {
            setErrorMessage("Seu carrinho está vazio");
            setTimeout(() => setErrorMessage(""), 3000);
            return;
        }

        // Validar que CEP e Cidade batem com o frete calculado
        if (!pickupOnStore && deliveryInfo) {
            const normalizedFormCep = data.postal_code?.replace(/\D/g, '');
            const normalizedDeliveryCep = deliveryInfo.cep?.replace(/\D/g, '');
            const cityMatch = data.city?.trim().toLowerCase() === deliveryInfo.city?.trim().toLowerCase();
            if (normalizedFormCep !== normalizedDeliveryCep || !cityMatch) {
                setErrorMessage(
                    `O CEP e a cidade do endereço (${data.city}) devem ser os mesmos utilizados no cálculo do frete (${deliveryInfo.city}).`
                );
                setTimeout(() => setErrorMessage(""), 6000);
                return;
            }
        }

        setShowLoader(true);

        const description = cartItems
            .map((p) => `${p.quantity}x ${p.name} - R$ ${p.price}`)
            .join('\n');

        const products = cartItems.map(item => ({
            id: item.id,
            name: item.name,
            price: item.price,
            quantity: item.quantity
        }));

        // Obter store_id do sessionStorage (salvo no StoreFront)
        const storefrontStoreId = sessionStorage.getItem('storefront_store_id');

        const orderData = {
            store_id: storefrontStoreId,
            store_slug: slug,
            client_id: client_id,
            phone_number: rawTelephone(data.phone_number),
            country_code: data.country_code,
            first_name: data.first_name,
            last_name: data.last_name,
            order_email: data.email,
            pickup_on_store: pickupOnStore,
            address_id: addressId,
            type_of_delivery: data.type_of_delivery,
            street: data.street,
            street_number: data.street_number,
            complement: data.complement,
            reference_point: data.reference_point,
            neighborhood: data.neighborhood,
            city: data.city,
            state: data.state,
            postal_code: data.postal_code,
            country: data.country,
            receiver_name: data.receiver_name,
            receiver_phone: data.receiver_phone ? rawTelephone(data.receiver_phone) : "",
            receiver_country_code: data.receiver_country_code,
            delivery_date: data.delivery_date,
            has_card: data.has_card,
            card_message: data.card_message,
            card_from: data.card_from,
            card_to: data.card_to,
            description: description,
            additional_information: "",
            payment_method: "MERCADO_PAGO",
            payment_received: false,
            products_value: cartTotal,
            discount: couponDiscount,
            delivery_fee: deliveryFee,
            total: totalWithDelivery,
            status: 'PENDING_PAYMENT',
            products: products,
            is_delivery: !pickupOnStore,
            online_order: false,
            store_front_order: true,
            coupon_code: appliedCoupon?.code,
            coupon_id: appliedCoupon?.coupon_id,
        };

        try {
            const { data: response } = await createOrder({
                clientId: client_id,
                ...orderData,
            });

            // Criar preferência de pagamento e redirecionar para o Mercado Pago
            if (slug) {
                try {
                    const baseUrl = window.location.origin;

                    // Aplica o desconto do cupom proporcionalmente ao preço de cada item,
                    // já que a preferência do Mercado Pago não possui campo de desconto.
                    const discountRatio = cartTotal > 0 ? couponDiscount / cartTotal : 0;
                    const discountedItems = cartItems.map(item => ({
                        ...item,
                        discountedUnitPrice: Math.round((item.price || 0) * (1 - discountRatio) * 100) / 100
                    }));

                    if (couponDiscount > 0 && discountedItems.length > 0) {
                        const roundedTotal = discountedItems.reduce(
                            (sum, item) => sum + item.discountedUnitPrice * item.quantity, 0
                        );
                        const expectedTotal = Math.round((cartTotal - couponDiscount) * 100) / 100;
                        const roundingDiff = Math.round((expectedTotal - roundedTotal) * 100) / 100;

                        if (roundingDiff !== 0) {
                            const lastItem = discountedItems[discountedItems.length - 1];
                            lastItem.discountedUnitPrice = Math.round(
                                (lastItem.discountedUnitPrice + roundingDiff / lastItem.quantity) * 100
                            ) / 100;
                        }
                    }

                    const preferenceData = {
                        order_id: response.id,
                        store_slug: slug,
                        order_email: data.email,
                        items: discountedItems.map(item => ({
                            id: item.id || '',
                            title: item.name,
                            description: item.name,
                            picture_url: item.image,
                            quantity: item.quantity,
                            unit_price: item.discountedUnitPrice,
                            currency_id: 'BRL'
                        })),
                        shipments: {
                            cost: deliveryFee,
                            mode: 'not_specified'
                        },
                        payer: {
                            name: data.first_name,
                            surname: data.last_name,
                            ...(data.country_code === "BR" ? {
                                phone: {
                                    area_code: rawTelephone(data.phone_number).substring(0, 2),
                                    number: rawTelephone(data.phone_number).substring(2)
                                }
                            } : {})
                        },
                        back_urls: {
                            success: `${baseUrl}/${slug}/checkout/success`,
                            failure: `${baseUrl}/${slug}/checkout/failure`,
                            pending: `${baseUrl}/${slug}/checkout/pending`
                        },
                        auto_return: 'approved' as const
                    };

                    const preference = await createMercadoPagoPreference(preferenceData);
                    
                    // Redirecionar para o checkout do Mercado Pago
                    // Em produção usar init_point, em desenvolvimento usar sandbox_init_point
                    const checkoutUrl = process.env.NODE_ENV === 'production' 
                        ? preference.init_point 
                        : preference.sandbox_init_point;
                    
                    // Manter loader ativo durante o redirecionamento
                    window.location.href = checkoutUrl;
                    return;
                } catch (mpError: any) {
                    console.error("Erro ao criar preferência de pagamento:", mpError);
                    setErrorMessage("Erro ao processar pagamento. Tente novamente.");
                    setTimeout(() => setErrorMessage(""), 3000);
                    setShowLoader(false);
                    return;
                }
            }
        } catch (error: any) {
            setErrorMessage("Erro ao criar pedido. Tente novamente.");
            setTimeout(() => setErrorMessage(""), 3000);
            setShowLoader(false);
        }
    };

    const typeOfDelivery = watch("type_of_delivery");
    const hasCard = watch("has_card");
    const watchPhoneNumber = watch("phone_number");

    // Busca automática desativada por questões de LGPD
    // useEffect(() => {
    //     const fetchClientData = async () => {
    //         const phoneNumber = rawTelephone(phone_number);
    //
    //         if (phoneNumber && phoneNumber.length >= 10 && phoneNumber !== identifiedUser) {
    //             try {
    //                 setShowLoader(true);
    //                 const response = await getClientByPhone(phoneNumber);
    //                 const { data: client } = response;
    //
    //                 if (!client) {
    //                     setClientId("");
    //                     setValue("first_name", "");
    //                     setValue("last_name", "");
    //                     setShowLoader(false);
    //                 }
    //
    //                 if (client) {
    //                     setValue("first_name", client.first_name);
    //                     setValue("last_name", client.last_name);
    //                     setClientId(client.id);
    //                     const { data: addresses } = await getClientAddresses(client.id);
    //
    //                     if (addresses) {
    //                         setAddresses(addresses);
    //                     }
    //
    //                     setShowLoader(false);
    //
    //                     if (!hasShownWelcomeModal.current || identifiedUser !== client.phone_number) {
    //                         setIdentifiedUser(client.phone_number);
    //                         setShowWelcomeModal(true);
    //                         hasShownWelcomeModal.current = true;
    //                     }
    //                 }
    //             } catch (error) {
    //                 setClientId("");
    //                 setShowLoader(false);
    //             }
    //         }
    //     };
    //
    //     setTimeout(() => {
    //         fetchClientData();
    //     }, 900);
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [phone_number, setValue]);

    // Carregar schedules da loja do sessionStorage
    useEffect(() => {
        const savedSchedules = sessionStorage.getItem('storefront_store_schedules');
        if (savedSchedules) {
            try {
                setStoreSchedules(JSON.parse(savedSchedules));
            } catch (error) {
                console.error('Erro ao carregar schedules do sessionStorage:', error);
            }
        }
        
        // Carregar max_delivery_days_advance do sessionStorage
        const savedMaxDeliveryDays = sessionStorage.getItem('storefront_max_delivery_days_advance');
        if (savedMaxDeliveryDays) {
            setMaxDeliveryDaysAdvance(Number(savedMaxDeliveryDays) || 30);
        }
    }, []);

    useEffect(() => {
        if (cartItems.length === 0 && !currentOrder) {
            navigate(`/${slug}`);
        }
    }, [cartItems, currentOrder, navigate, slug]);

    // Redirecionar ao carrinho se o frete não foi calculado
    useEffect(() => {
        if (!isDeliveryCalculated && cartItems.length > 0) {
            navigate(`/${slug}/carrinho`);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Preencher campos de endereço com dados do cálculo de frete
    useEffect(() => {
        if (deliveryInfo) {
            setValue("postal_code", deliveryInfo.cep);
            setValue("city", deliveryInfo.city);
            setValue("state", deliveryInfo.state);
            setValue("street", deliveryInfo.street);
            setValue("neighborhood", deliveryInfo.neighborhood);
        }
    }, [deliveryInfo, setValue]);

    useEffect(() => {
        if (currentOrder?.id) {
            const timer = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        navigate(`/${slug}`);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(timer);
        }
    }, [currentOrder, navigate, slug]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            let errorMessage = "";

            Object.values(errors).forEach((error) => {
                if (error?.message) {
                    errorMessage += `${error.message}\n - `;
                }
            });

            setErrorMessage(errorMessage);

            setTimeout(() => {
                setErrorMessage("");
            }, 5000);
        } else {
            setErrorMessage("");
        }
    }, [submitCount, errors]);

    // Resetar formulário quando o telefone for alterado
    useEffect(() => {
        const phoneNumber = watch("phone_number") || "";
        const numericValue = rawTelephone(phoneNumber);

        // Se já tinha iniciado o formulário e o telefone mudou
        if (formStarted && numericValue.length >= 10) {
            const previousPhone = previousPhoneRef.current;
            
            // Se o telefone realmente mudou
            if (previousPhone && previousPhone !== numericValue) {
                // Resetar estados e campos
                setClientId("");
                setClientExists(false);
                setAwaitingVerification(false);
                setFormStarted(false);
                setValue("first_name", "");
                setValue("last_name", "");
                setValue("verification_code", "");
                setAddresses([]);
                clearErrors(["first_name", "last_name", "verification_code"]);
            }
        }
        
        // Atualizar o telefone anterior
        if (numericValue.length >= 10) {
            previousPhoneRef.current = numericValue;
        }
    }, [watchPhoneNumber, formStarted, watch, setValue, clearErrors]);

    const handlePickUpAddress = async (value: boolean) => {
        if (value) {
            const { data: pickUpAddress } = await getPickupAddress() as any;
            setAddressId(pickUpAddress.id);
            setValue("addressId", pickUpAddress.id);
            setValue("pickup_on_store", true);
        }

        if (!value) {
            setValue("addressId", "");
            setValue("pickup_on_store", false);
        }
    };

    const handleWriteMessageClick = () => {
        setValue("has_card", true);
        setShowRememberCardModal(false);

        setTimeout(() => {
            cardSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
    };

    const handleNoMessageClick = () => {
        setShowRememberCardModal(false);
        handleSubmit(submitOrder)();
    };

    const handleCreateUser = async () => {
        const phoneNumber = watch('phone_number');
        const email = watch('email');
        const firstName = watch('first_name');
        const lastName = watch('last_name');

        if (!phoneNumber || !email) {
            setError("phone_number", { message: "Preencha o telefone e o email" });
            setError("email", { message: "Preencha o telefone e o email" });
            return;
        }

        setError("phone_number", { message: "" });
        setError("email", { message: "" });

        setShowLoader(true);
        setFormStarted(true);

        try {
            const { data } = await requestVerificationCode({
                phone_number: rawTelephone(phoneNumber),
                email: email,
            });

            // Armazenar o telefone atual como referência
            previousPhoneRef.current = rawTelephone(phoneNumber);

            if (data.client_exists) {
                // Cliente existe, solicitar código de verificação
                setClientExists(true);
                setAwaitingVerification(true);
                setVerificationSentVia(data.sent_via || "whatsapp");
                setWelcomeClientName(data.first_name);
                setShowWelcomeModal(true);
            } else {
                // Cliente não existe, criar novo cliente
                if (!firstName || !lastName) {
                    setError("first_name", { message: "Preencha seu nome" });
                    setError("last_name", { message: "Preencha seu sobrenome" });
                    setShowLoader(false);
                    return;
                }

                const { data: clientData } = await createClientOnline({
                    first_name: firstName,
                    last_name: lastName,
                    phone_number: rawTelephone(phoneNumber),
                    country_code: watch("country_code") || "BR",
                    email: email,
                });

                setClientId(clientData.id);
                clearErrors(["first_name", "last_name"]);
                setTimeout(() => { setSuccessMessage('') }, 3000);
            }
        } catch (error: any) {
            console.error(error);            
            let errorMsg = 'Algo deu errado, por favor tente novamente';
            
            setErrorMessage(errorMsg);
            setTimeout(() => { setErrorMessage('') }, 5000);
        } finally {
            setShowLoader(false);
        }
    };

    const handleValidateCode = async () => {
        const phoneNumber = watch('phone_number');
        const code = watch('verification_code');

        if (!code || code.length !== 6) {
            setError("verification_code", { message: "Digite o código de 6 dígitos" });
            return;
        }

        setError("verification_code", { message: "" });
        setShowLoader(true);

        try {
            const { data } = await validateVerificationCode({
                phone_number: rawTelephone(phoneNumber),
                code: code,
            });

            if (data.valid && data.client) {
                // Preencher dados do cliente
                setValue("first_name", data.client.first_name);
                setValue("last_name", data.client.last_name);
                setValue("country_code", data.client.country_code || "BR");
                // Email já está preenchido pelo usuário, não precisa setValue
                
                // Limpar erros de validação dos campos preenchidos
                clearErrors(["first_name", "last_name"]);
                
                setClientId(data.client.id);

                // Carregar endereços
                if (data.client.addresses && data.client.addresses.length > 0) {
                    setAddresses(data.client.addresses);
                }

                setAwaitingVerification(false);
                setSuccessMessage('Código validado com sucesso!');
                setTimeout(() => { setSuccessMessage('') }, 3000);
            }
        } catch (error: any) {
            console.error(error);
            const errorMsg = error?.response?.data?.message || 'Código inválido ou expirado';
            setError("verification_code", { message: errorMsg });
            setErrorMessage(errorMsg);
            setTimeout(() => { setErrorMessage('') }, 3000);
        } finally {
            setShowLoader(false);
        }
    };

    const handleResendCodeByEmail = async () => {
        const phoneNumber = watch('phone_number');
        const email = watch('email');

        if (!phoneNumber || !email) {
            return;
        }

        setResendingEmail(true);

        try {
            await resendVerificationCodeEmail({
                phone_number: rawTelephone(phoneNumber),
                email: email,
            });

            setVerificationSentVia("email");
            setSuccessMessage('Código reenviado para o seu e-mail!');
            setTimeout(() => { setSuccessMessage('') }, 3000);
        } catch (error: any) {
            console.error(error);
            const errorMsg = error?.response?.data?.message || 'Não foi possível reenviar o código, tente novamente';
            setErrorMessage(errorMsg);
            setTimeout(() => { setErrorMessage('') }, 3000);
        } finally {
            setResendingEmail(false);
        }
    };

    return (
        <Container>
            <WelcomeBackModal
                isOpen={showWelcomeModal}
                onRequestClose={() => setShowWelcomeModal(false)}
                name={welcomeClientName}
                textBody={
                    verificationSentVia === "whatsapp"
                        ? "Digite o código enviado para o seu WhatsApp para confirmar sua identidade."
                        : "Digite o código enviado para o seu e-mail para confirmar sua identidade."
                }
                spamNotice={
                    verificationSentVia === "email"
                        ? "Não recebeu? Verifique também as pastas de Spam e Lixo Eletrônico."
                        : undefined
                }
            />
            <RememberCardModal
                isOpen={showRememberCardModal}
                onRequestClose={() => setShowRememberCardModal(false)}
                handleWriteMessageClick={handleWriteMessageClick}
                handleNoMessageClick={handleNoMessageClick}
            />
            <TooltipModal
                isOpen={showToolTipModal}
                onRequestClose={() => setShowToolTipModal(false)}
                textContent={tooltipMessage}
                title=""
                showWhatsapp
                showCopyButton={false}
            />
            <Loader show={showLoader} />
            {errorMessage && <ErrorAlert message={errorMessage} />}
            {successMessage && <SuccessAlert message={successMessage} />}

            <StoreFrontHeader 
                showBackButton 
                backButtonText="Voltar ao Carrinho"
                backButtonPath={`/${slug}/carrinho`}
                slug={slug}
            />

            <StepperContainer>
                <StepperWrapper>
                    {["Carrinho", "Informações do Pedido", "Pagamento"].map((stepName, index) => {
                        const stepNumber = index + 1;
                        const currentStep = 2;
                        const isActive = currentStep === stepNumber;
                        const isCompleted = currentStep > stepNumber;
                        const isClickable = currentStep > stepNumber;
                        
                        return (
                            <Step 
                                key={stepNumber} 
                                active={isActive} 
                                completed={isCompleted}
                                clickable={isClickable}
                                onClick={() => isClickable && navigate(`/${slug}/carrinho`)}
                            >
                                <StepCircle active={isActive} completed={isCompleted}>
                                    {isCompleted ? '✓' : stepNumber}
                                </StepCircle>
                                <StepLabel active={isActive}>{stepName}</StepLabel>
                                <StepSubLabel>
                                    {stepNumber === 1 && 'Revise seus itens'}
                                    {stepNumber === 2 && 'Dados de entrega'}
                                    {stepNumber === 3 && 'Finalize o pedido'}
                                </StepSubLabel>
                            </Step>
                        );
                    })}
                </StepperWrapper>
            </StepperContainer>

            <Content>
                {!currentOrder?.id &&
                    <>
                        <CartSection>
                            <GlobalForm onSubmit={handleSubmit(submitOrder)}>
                                <FormFieldTitle>Dados do Comprador</FormFieldTitle>
                                <FormField>
                                    <Label>
                                        Seu telefone
                                        <span>*</span>
                                    </Label>
                                    <PhoneInput
                                        control={control}
                                        setValue={setValue}
                                        phoneFieldName="phone_number"
                                        countryFieldName="country_code"
                                        disabled={awaitingVerification}
                                        required
                                    />
                                    {errors.phone_number && <ErrorMessage>{errors.phone_number.message}</ErrorMessage>}
                                </FormField>
                                <FormField>
                                    <Label>
                                        Seu email
                                        <span>*</span>
                                    </Label>
                                    <Input 
                                        type="email" 
                                        autoComplete="email" 
                                        placeholder="Digite seu email"
                                        disabled={awaitingVerification || !!client_id}
                                        {...register("email", {
                                            required: "Email inválido",
                                            pattern: {
                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                message: "Email inválido"
                                            }
                                        })}
                                    />
                                    {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
                                </FormField>

                                {awaitingVerification && (
                                    <FormField>
                                        <Label>
                                            Código de Verificação
                                            <span>*</span>
                                        </Label>
                                        <Input
                                            type="text"
                                            autoComplete="off"
                                            placeholder={
                                                verificationSentVia === "whatsapp"
                                                    ? "Digite o código enviado para o WhatsApp"
                                                    : "Digite o código enviado para o email"
                                            }
                                            maxLength={6}
                                            {...register("verification_code", {
                                                required: "Digite o código",
                                                minLength: {
                                                    value: 6,
                                                    message: "Código deve ter 6 dígitos"
                                                },
                                                maxLength: {
                                                    value: 6,
                                                    message: "Código deve ter 6 dígitos"
                                                }
                                            })}
                                        />
                                        {errors.verification_code && <ErrorMessage>{errors.verification_code.message}</ErrorMessage>}
                                        <PrimaryButton type="button" onClick={handleValidateCode} style={{ marginTop: '10px' }}>
                                            Validar Código
                                        </PrimaryButton>
                                        {verificationSentVia === "whatsapp" && (
                                            <ResendCodeLink
                                                type="button"
                                                onClick={handleResendCodeByEmail}
                                                disabled={resendingEmail}
                                            >
                                                {resendingEmail ? "Enviando..." : "Não recebeu no WhatsApp? Enviar código por e-mail"}
                                            </ResendCodeLink>
                                        )}
                                    </FormField>
                                )}

                                {((formStarted && !awaitingVerification && !clientExists) || client_id) && (
                                    <InlineFormField>
                                        <FormField>
                                            <Label>
                                                Seu nome
                                                <span>*</span>
                                            </Label>
                                            <Input 
                                                type="text" 
                                                autoComplete="off" 
                                                placeholder="Digite seu nome"
                                                disabled={!!client_id}
                                                {...register("first_name", {
                                                    required: "Nome inválido",
                                                })}
                                            />
                                            {errors.first_name && <ErrorMessage>{errors.first_name.message}</ErrorMessage>}
                                        </FormField>
                                        <FormField>
                                            <Label>
                                                Seu sobrenome
                                                <span>*</span>
                                            </Label>
                                            <Input 
                                                type="text" 
                                                placeholder="Digite seu sobrenome"
                                                disabled={!!client_id}
                                                {...register("last_name", {
                                                    required: "Sobrenome inválido",
                                                })}
                                            />
                                            {errors.last_name && <ErrorMessage>{errors.last_name.message}</ErrorMessage>}
                                        </FormField>
                                    </InlineFormField>
                                )}

                                {!client_id && !awaitingVerification &&
                                    <PrimaryButton type="button" onClick={handleCreateUser}>
                                        Continuar
                                    </PrimaryButton>
                                }

                                {client_id && (
                                    <>
                                        <FormFieldTitle>Informações de Entrega</FormFieldTitle>

                                        <FormField>
                                            <Label>CEP</Label>
                                            <Input
                                                type="text"
                                                disabled
                                                {...register("postal_code")}
                                            />
                                        </FormField>

                                        <FormField>
                                            <Label>
                                                Informe sobre a entrega
                                                <span>*</span>
                                            </Label>
                                            <Select {...register("type_of_delivery", { required: "Campo obrigatório" })}>
                                                <option value="">Escolher:</option>
                                                {Object.entries(TYPES_OF_DELIVERY).map(([key, value]) => (
                                                    <option key={key} value={key}>{value}</option>
                                                ))}
                                            </Select>
                                            {errors.type_of_delivery && <ErrorMessage>{errors.type_of_delivery.message}</ErrorMessage>}
                                        </FormField>

                                        {typeOfDelivery === "SOMEONES_GIFT" &&
                                            <InlineFormField>
                                                <FormField>
                                                    <Label>
                                                        Recebedor
                                                        <span>*</span>
                                                    </Label>
                                                    <Input type="text" placeholder="Recebedor"
                                                        {...register("receiver_name", {
                                                            required: "Nome inválido",
                                                        })}
                                                    />
                                                    {errors.receiver_name && <ErrorMessage>{errors.receiver_name.message}</ErrorMessage>}
                                                </FormField>
                                                <FormField>
                                                    <Label>
                                                        Telefone do recebedor
                                                        <span>*</span>
                                                    </Label>
                                                    <PhoneInput
                                                        control={control}
                                                        setValue={setValue}
                                                        phoneFieldName="receiver_phone"
                                                        countryFieldName="receiver_country_code"
                                                        required
                                                    />
                                                    {errors.receiver_phone && <ErrorMessage>{errors.receiver_phone.message}</ErrorMessage>}
                                                </FormField>
                                            </InlineFormField>
                                        }

                                        {addresses.length === 0 &&
                                            <CheckboxContainer alignLeft>
                                                <Checkbox type="checkbox" onChange={(e) => {
                                                    setPickupOnStore(!pickupOnStore)
                                                    handlePickUpAddress(e.target.checked);
                                                }} checked={pickupOnStore} />
                                                <Label>Retirar Pedido na loja</Label>
                                            </CheckboxContainer>
                                        }

                                        {addresses.length > 0 && (
                                            <>
                                                {!pickupOnStore && (() => {
                                                    const compatibleAddresses = addresses.filter((address: any) => {
                                                        const addrCep = address.postal_code?.replace(/\D/g, '');
                                                        const freightCep = deliveryInfo?.cep?.replace(/\D/g, '');
                                                        const cityOk = address.city?.trim().toLowerCase() === deliveryInfo?.city?.trim().toLowerCase();
                                                        return addrCep === freightCep && cityOk;
                                                    });
                                                    return (
                                                        <FormField>
                                                            <Label>
                                                                Selecione o Endereço (Ou cadastre um novo)
                                                                <span>*</span>
                                                            </Label>
                                                            {compatibleAddresses.length === 0 && (
                                                                <ErrorMessage>
                                                                    Nenhum endereço salvo corresponde ao CEP/cidade do frete calculado. Cadastre um novo endereço abaixo.
                                                                </ErrorMessage>
                                                            )}
                                                            <Select
                                                                {...register("addressId")}
                                                                onChange={(e) => {
                                                                    const selectedAddressId = e.target.value;
                                                                    const selectedAddress: any = compatibleAddresses.find((address: any) => address.id === selectedAddressId);
                                                                    setSelectedAddress(selectedAddress);
                                                                    if (selectedAddress) {
                                                                        // Preenche os campos editáveis do endereço salvo,
                                                                        // mas mantém CEP, cidade e estado fixos do frete
                                                                        setValue("street", selectedAddress.street);
                                                                        setValue("street_number", selectedAddress.street_number);
                                                                        setValue("neighborhood", selectedAddress.neighborhood);
                                                                        setValue("complement", selectedAddress.complement);
                                                                        setValue("reference_point", selectedAddress.reference_point);
                                                                        setValue("country", selectedAddress.country || "Brasil");
                                                                        // CEP, cidade e estado sempre do frete calculado
                                                                        setValue("postal_code", deliveryInfo?.cep || "");
                                                                        setValue("city", deliveryInfo?.city || "");
                                                                        setValue("state", deliveryInfo?.state || "");
                                                                        setAddressId(selectedAddressId);
                                                                    }
                                                                    if (!selectedAddress) {
                                                                        setValue("street", deliveryInfo?.street || "");
                                                                        setValue("street_number", "");
                                                                        setValue("neighborhood", deliveryInfo?.neighborhood || "");
                                                                        setValue("complement", "");
                                                                        setValue("reference_point", "");
                                                                        // Sempre do frete
                                                                        setValue("postal_code", deliveryInfo?.cep || "");
                                                                        setValue("city", deliveryInfo?.city || "");
                                                                        setValue("state", deliveryInfo?.state || "");
                                                                        setAddressId("");
                                                                    }
                                                                }}
                                                            >
                                                                <option value="">Selecione um endereço</option>
                                                                {compatibleAddresses.map((address: any) => (
                                                                    <option key={address.id} value={address.id}>
                                                                        {address.street}, {address.street_number} - {address.city}
                                                                    </option>
                                                                ))}
                                                            </Select>
                                                        </FormField>
                                                    );
                                                })()}

                                                <InlineFormField>
                                                    <CheckboxContainer>
                                                        <Checkbox type="checkbox" disabled={pickupOnStore} onChange={() => {
                                                            setNewAddress(!newAddress)

                                                            if (!newAddress) {
                                                                setAddressId("");
                                                                setValue("addressId", "");
                                                            }

                                                            if (newAddress) {
                                                                setAddressId(selectedAddress.id);
                                                                setValue("addressId", selectedAddress.id);
                                                            }

                                                        }} checked={newAddress} />
                                                        <Label noMargin>Cadastrar novo endereço</Label>
                                                    </CheckboxContainer>

                                                    <CheckboxContainer>
                                                        <Checkbox type="checkbox" onChange={(e) => {
                                                            setPickupOnStore(!pickupOnStore)
                                                            handlePickUpAddress(e.target.checked);

                                                        }} checked={pickupOnStore} />
                                                        <Label noMargin>Retirar na Loja</Label>
                                                    </CheckboxContainer>
                                                </InlineFormField>
                                            </>
                                        )}

                                        {!pickupOnStore &&
                                            <>
                                                <FormField>
                                                    <Label>
                                                        Rua
                                                        <span>*</span>
                                                    </Label>
                                                    <Input type="text" placeholder="Rua" {...register("street", {
                                                        required: "Rua inválida",
                                                    })}
                                                        disabled={(addresses.length > 0 && !newAddress) ? true : false}
                                                    />
                                                    {errors.street && <ErrorMessage>{errors.street.message}</ErrorMessage>}
                                                </FormField>
                                                <InlineFormField>
                                                    <FormField isShortField>
                                                        <Label>
                                                            Número
                                                            <span>*</span>
                                                        </Label>
                                                        <Input type="text" placeholder="Número" {...register("street_number", {
                                                            required: "Número inválido",
                                                        })}
                                                            disabled={(addresses.length > 0 && !newAddress) ? true : false}
                                                        />
                                                        {errors.street_number && <ErrorMessage>{errors.street_number.message}</ErrorMessage>}
                                                    </FormField>
                                                    <FormField>
                                                        <Label>
                                                            Complemento
                                                        </Label>
                                                        <Input type="text" placeholder="Complemento" {...register("complement")}
                                                            disabled={(addresses.length > 0 && !newAddress) ? true : false}
                                                        />
                                                    </FormField>
                                                </InlineFormField>
                                                <FormField>
                                                    <Label>
                                                        Bairro
                                                        <span>*</span>
                                                    </Label>
                                                    <Input type="text" placeholder="Bairro" {...register("neighborhood", {
                                                        required: "Bairro inválido",
                                                    })}
                                                        disabled={(addresses.length > 0 && !newAddress) ? true : false}
                                                    />
                                                    {errors.neighborhood && <ErrorMessage>{errors.neighborhood.message}</ErrorMessage>}
                                                </FormField>
                                                <FormField>
                                                    <Label>Ponto de referência</Label>
                                                    <Input type="text" placeholder="Ponto de referência" {...register("reference_point")}
                                                        disabled={(addresses.length > 0 && !newAddress) ? true : false}
                                                    />
                                                </FormField>

                                                <InlineFormField>
                                                    <FormField>
                                                        <Label>
                                                            Estado
                                                            <span>*</span>
                                                        </Label>
                                                        <Input
                                                            type="text"
                                                            disabled
                                                            {...register("state", { required: "Estado inválido" })}
                                                        />
                                                        {errors.state && <ErrorMessage>{errors.state.message}</ErrorMessage>}
                                                    </FormField>
                                                    <FormField>
                                                        <Label>
                                                            Cidade
                                                            <span>*</span>
                                                        </Label>
                                                        <Input
                                                            type="text"
                                                            disabled
                                                            {...register("city", { required: "Cidade inválida" })}
                                                        />
                                                        {errors.city && <ErrorMessage>{errors.city.message}</ErrorMessage>}
                                                    </FormField>
                                                </InlineFormField>
                                            </>
                                        }

                                        <FormField>
                                            <Label>
                                                Data de Entrega
                                                <span>*</span>
                                            </Label>
                                            <Input 
                                                type="date" 
                                                min={moment().format('YYYY-MM-DD')}
                                                max={moment().add(maxDeliveryDaysAdvance, 'days').format('YYYY-MM-DD')}
                                                {...register("delivery_date", {
                                                    required: "Data de entrega é obrigatória",
                                                    validate: (value) => {
                                                        if (!isValidDeliveryDate(value)) {
                                                            const selectedDate = moment(value);
                                                            const dayName = selectedDate.format('dddd');
                                                            const maxDate = moment().add(maxDeliveryDaysAdvance, 'days');
                                                            
                                                            // Verificar se é hoje e o horário já passou
                                                            if (selectedDate.isSame(moment(), 'day')) {
                                                                return "O horário de funcionamento da loja já passou para hoje. Escolha outra data.";
                                                            }
                                                            
                                                            // Verificar se excede o limite de dias de antecedência
                                                            if (selectedDate.isAfter(maxDate, 'day')) {
                                                                return `Desculpe, só é possível agendar entregas até ${maxDeliveryDaysAdvance} dias de antecedência.`;
                                                            }
                                                            
                                                            return `A loja não funciona ${dayName}. Por favor, escolha outro dia.`;
                                                        }
                                                        return true;
                                                    }
                                                })} 
                                            />
                                            {errors.delivery_date && <ErrorMessage>{errors.delivery_date.message}</ErrorMessage>}
                                            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '8px' }}>
                                                📅 Você pode agendar sua entrega com até {maxDeliveryDaysAdvance} dias de antecedência.
                                            </p>
                                        </FormField>

                                        <FormField>
                                            <CheckboxContainer>
                                                <Checkbox type="checkbox" {...register("has_card")} />
                                                <Label>Pedido Contém Cartão de Mensagem.</Label>
                                            </CheckboxContainer>
                                        </FormField>

                                        {hasCard &&
                                            <>
                                                <FormField ref={cardSectionRef}>
                                                    <Label>
                                                        De: <span>*</span>
                                                    </Label>
                                                    <Input {...register("card_from", {
                                                        required: "Remetente do cartão é obrigatório",
                                                    })} />
                                                    {errors.card_from && <ErrorMessage>{errors.card_from.message}</ErrorMessage>}
                                                </FormField>
                                                <FormField>
                                                    <Label>
                                                        Para: <span>*</span>
                                                    </Label>
                                                    <Input {...register("card_to", {
                                                        required: "Destinatário do cartão é obrigatório",
                                                    })} />
                                                    {errors.card_to && <ErrorMessage>{errors.card_to.message}</ErrorMessage>}
                                                </FormField>
                                                <FormField>
                                                    <Label>Mensagem do cartão <span>*</span></Label>
                                                    <Textarea {...register("card_message", { required: "Mensagem do cartão é obrigatório", })}
                                                    />
                                                    {errors.card_message && <ErrorMessage>{errors.card_message.message}</ErrorMessage>}
                                                </FormField>
                                            </>
                                        }

                                        <PrimaryButton type="submit">Finalizar Pedido</PrimaryButton>
                                    </>
                                )}
                            </GlobalForm>
                        </CartSection>

                        <OrderSummary>
                            <SectionTitle>Resumo do Pedido</SectionTitle>
                            
                            {cartItems.length > 0 && (
                                <>
                                    <ProductsList>
                                        {cartItems.map((item) => (
                                            <ProductItem key={item.id}>
                                                <ProductDetails>
                                                    <CartItemImage
                                                        src={item.image}
                                                        alt={item.name}
                                                    />
                                                    <div>
                                                        <ProductName>{item.name}</ProductName>
                                                        <ProductQuantity>Quantidade: {item.quantity}</ProductQuantity>
                                                    </div>
                                                </ProductDetails>
                                                <ProductPrice>
                                                    {convertMoney((item.price || 0) * item.quantity)}
                                                </ProductPrice>
                                            </ProductItem>
                                        ))}
                                    </ProductsList>
                                    <Divider />
                                </>
                            )}
                            
                            <SummaryRow>
                                <span>Subtotal:</span>
                                <span>{convertMoney(cartTotal)}</span>
                            </SummaryRow>
                            <SummaryRow>
                                <span>Taxa de Entrega:</span>
                                <span>{convertMoney(deliveryFee)}</span>
                            </SummaryRow>
                            {appliedCoupon && couponDiscount > 0 && (
                                <SummaryRow>
                                    <span>Desconto ({appliedCoupon.code}):</span>
                                    <span>-{convertMoney(couponDiscount)}</span>
                                </SummaryRow>
                            )}
                            <SummaryRow className="total">
                                <span>Total:</span>
                                <span>{convertMoney(totalWithDelivery)}</span>
                            </SummaryRow>
                        </OrderSummary>
                    </>
                }
            </Content>

            {currentOrder &&
                <CompletedOrder>
                    <h1>Seu pedido #{currentOrder?.code} foi enviado para a loja!</h1>
                    {!currentOrder?.payment_received &&
                        <>
                            <h2>Para concluir, finalize o pagamento com nosso atendente no WhatsApp.</h2>
                            <h2>Assim que confirmado, daremos sequência ao seu pedido.</h2>
                            <p>🌺🌻🌸</p>
                        </>
                    }

                    {currentOrder?.payment_received &&
                        <h2>Qualquer dúvida entre em contato conosco.</h2>
                    }

                    <p style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
                        Você será redirecionado para a página inicial em {countdown} segundo{countdown !== 1 ? 's' : ''}...
                    </p>
                </CompletedOrder>
            }
        </Container>
    );
}
